<script setup lang="ts">
const props = defineProps({
    currentIndex: {
        type: Number,
        required: true
    },
    perPage: {
        type: Number,
        required: true
    },
    total: {
        type: Number,
        default: 0
    },
    paginatedUrl: {
        type: String,
        required: true
    }
})

const maxPage = computed(() =>
    props.total % props.perPage === 0
        ? props.total / props.perPage
        : (props.total - (props.total % props.perPage)) / props.perPage + 1
)

const pagesBefore = computed<number[]>(() => {
    const result = []
    for (let i = 1; i > 0; i -= 1) {
        const prevValue = props.currentIndex - i

        if (prevValue > 0) {
            result.push(prevValue)
        }
    }

    return result
})

const pagesAfter = computed<number[]>(() => {
    const result = []
    for (let i = 1; i <= 1; i += 1) {
        const nextValue = props.currentIndex + i

        if (nextValue < maxPage.value) {
            result.push(nextValue)
        }
    }

    return result
})

const replacePage = (pageIndex: number) =>
    props.paginatedUrl.replace('{page}', pageIndex.toString())
</script>

<template>
    <div class="pagination">
        <a
            rel="prev"
            :href="replacePage(currentIndex - 1)"
            :title="$t('word.previous')"
        >
            <button
                type="button"
                class="pagination--prev"
                :disabled="currentIndex === 1"
            >
                <IconChevronLeft is-solid />
            </button>
        </a>

        <a
            v-show="currentIndex > 1"
            :href="replacePage(1)"
            :title="$t('word.previous')"
        >
            <button
                type="button"
                class="pagination--page pagination--first-page"
            >
                1
            </button>
        </a>

        <div v-show="currentIndex > 3" class="pagination--page__hidden button">
            …
        </div>

        <a
            v-for="pageBefore in pagesBefore"
            v-show="currentIndex > 2"
            :key="pageBefore"
            :href="replacePage(pageBefore)"
            :title="$t('word.previous')"
        >
            <button type="button" class="pagination--page">
                {{ pageBefore }}
            </button>
        </a>

        <button
            type="button"
            class="pagination--page pagination--page__active"
            disabled
        >
            {{ currentIndex }}
        </button>

        <a
            v-for="pageAfter in pagesAfter"
            v-show="currentIndex > 2"
            :key="pageAfter"
            :href="replacePage(pageAfter)"
            :title="$t('word.next')"
        >
            <button type="button" class="pagination--page">
                {{ pageAfter }}
            </button>
        </a>

        <div
            v-show="
                currentIndex < maxPage - 1 && maxPage - 1 > currentIndex + 1
            "
            class="button pagination--page__hidden"
        >
            …
        </div>

        <a
            v-show="currentIndex < maxPage"
            :href="replacePage(maxPage)"
            :title="$t('word.next')"
        >
            <button
                type="button"
                class="pagination--page pagination--last-page"
            >
                {{ maxPage }}
            </button>
        </a>

        <a
            rel="next"
            :href="replacePage(currentIndex + 1)"
            :title="$t('word.next')"
        >
            <button
                type="button"
                class="pagination--next"
                :disabled="currentIndex === maxPage || total === 0"
            >
                <IconChevronRight is-solid />
            </button>
        </a>
    </div>
</template>

<style lang="scss" scoped>
.pagination {
    @apply flex gap-2;

    button,
    .button {
        @apply flex items-center justify-center rounded-full text-xxs md:text-inherit w-8 md:w-10 h-8 md:h-10 bg-zinc-100;

        &:disabled:not(.pagination--page__active) {
            @apply cursor-not-allowed text-gray-disabled;
        }
    }

    &--page {
        &:hover:not(:disabled) {
            @apply border border-[#AAE5FF] text-[#005085] bg-white;
        }

        &__active {
            @apply border border-[#0DA2E7] bg-[#F0FBFF] text-[#005085];
        }

        &:disabled {
            @apply cursor-not-allowed;
        }

        &__hidden {
            @apply cursor-default;
        }
    }

    &--prev svg,
    &--next svg {
        @apply w-6 h-6;
    }
}
</style>
